/**
 * Legacy store-page meta description v2 (owner 2026-10-28).
 *
 * The legacy template (stores with `seo_title_v2 === false`, see lib/store-seo `buildLegacyStoreMeta`) includes our ranking pages, so
 * its meta description may only change for the stores listed below. Every other legacy page keeps today's description byte for byte.
 * Ahrefs flags the old descriptions as too short (49-99 characters) and some are wrong ("1 active promo codes", "Latest Deal: [Deal]").
 * This module holds the allowlist and the pure text helpers of the new description; the wiring is in `buildLegacyStoreMeta`.
 *
 * Pure and unit-tested. Never puts a coupon code in the text.
 */

/**
 * Stores whose legacy meta description uses the v2 wording. Owner 2026-10-28: legacy pages with no Bing clicks or impressions in 60 days
 * (baseline: swiftpilot/rapoarte/meta-legacy-test-2026-10-28/). Phase 2 (runpod, seranking, coingate, hosthatch, famoid) and phase 3
 * (the 17 legacy pages with Bing traffic) are added only after the owner's 3-4 week check of this group.
 * Title, H1, H2 and everything else stay unchanged for all stores, these included.
 */
export const LEGACY_META_V2_STORES: ReadonlySet<string> = new Set([
    "aejuice", "leaseweb", "exacthosting", "upcloud", "typeset", "joinsecret", "dmca", "smartslider3", "12vpn", "vpsdime",
    "cloudnord", "proxystore", "anonymousproxies", "ai-fiesta", "iproyal", "datawagon", "ethernetservers", "akool", "pulsedmedia", "horizon",
    "hostvds", "ivpn", "themeforest", "descript", "massivegrid", "epik", "accsmarket", "trustzone", "bitbrowser", "rev",
    "salesflare", "envato", "servarica", "soax", "fineproxy", "noping", "decodo", "wps", "powr", "adspy",
    "captchas",
]);

export function isLegacyMetaV2Store(slug: string | null | undefined): boolean {
    return typeof slug === "string" && LEGACY_META_V2_STORES.has(slug);
}

/** The description is aimed at this window; 160 stays the hard maximum, as on the old template. */
export const LEGACY_META_V2_MIN = 120;
export const LEGACY_META_V2_SOFT_MAX = 155;
export const LEGACY_META_V2_HARD_MAX = 160;

// --- Which offer titles may follow "Latest Deal" ----------------------------------------------------------------------------

// Words that do not change what a title says: "Get a Free Gift", "Limited Time Offer" read like "Free Gift", "Offer".
const FILLER_WORDS = new Set([
    "get", "a", "an", "the", "your", "our", "this", "these", "now", "today", "new", "exclusive", "limited", "time",
    "great", "best", "amazing", "awesome", "huge", "big", "hot", "latest", "current",
]);

// A title that is only one of these (after the filler words are removed) tells the reader nothing.
const GENERIC_TITLES = new Set([
    "free gift", "free gifts", "gift", "gifts",
    "deal", "deals", "special deal", "special deals", "free deal",
    "special offer", "special offers", "offer", "offers", "free offer",
    "special bonus", "bonus", "free bonus",
    "special discount", "discount", "discounts",
    "free support", "free shipping",
    "sale", "sales",
    "promo", "promo code", "promo codes", "promocode",
    "coupon", "coupons", "coupon code", "coupon codes", "discount code", "discount codes",
    "free",
]);

// A title of fewer than three words is only worth quoting when it names something the visitor can buy or receive.
const CONCRETE_NOUNS = new Set([
    "plan", "plans", "vps", "vds", "server", "servers", "hosting", "host", "vpn", "proxy", "proxies", "domain", "domains", "ssl",
    "license", "licenses", "licence", "licences", "subscription", "subscriptions", "membership", "trial", "trials",
    "month", "months", "year", "years", "week", "weeks", "day", "days", "credit", "credits", "storage", "bandwidth", "ip", "ips",
    "theme", "themes", "plugin", "plugins", "template", "templates", "account", "accounts", "setup", "migration", "upgrade",
    "backup", "cloud", "email", "website", "app", "tool", "tools", "course", "courses", "ram", "cpu", "gb", "tb",
]);

function titleWords(title: string): string[] {
    return title.toLowerCase().split(/\s+/).map((w) => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")).filter((w) => w !== "");
}

/** The title is one of the generic labels (case-insensitive, filler words ignored) or has under three words and names nothing concrete. */
export function isGenericOfferTitle(title: string): boolean {
    const words = titleWords(title);
    if (words.length === 0) return true;
    const core = words.flatMap((w) => w.split(/[^a-z0-9]+/)).filter((w) => w !== "" && !FILLER_WORDS.has(w)).join(" ");
    if (core === "" || GENERIC_TITLES.has(core)) return true;
    return words.length < 3 && !words.some((w) => CONCRETE_NOUNS.has(w));
}

/** The only figure in the title is a percentage below 1 ("0.01% Off"): a scraped placeholder, not a discount. */
export function hasOnlyNonsensePercent(title: string): boolean {
    const percents = [...title.matchAll(/(\d+(?:\.\d+)?|\.\d+)\s*%/g)].map((m) => Number(m[1]));
    if (percents.length === 0 || !percents.every((p) => p < 1)) return false;
    return !/[$€£]\s?\d/.test(title);
}

/** An offer title that may be quoted after "Latest Deal". */
export function isUsableLatestDealTitle(title: string): boolean {
    return !isGenericOfferTitle(title) && !hasOnlyNonsensePercent(title);
}

// --- The description -------------------------------------------------------------------------------------------------------

/** "Up to 25% with 1 active promo code" / "3 active promo codes & deals" / "1 active promo code or deal": grammatical for 1. */
export function legacyClaimV2(activeCount: number, maxDiscount: number): string {
    if (maxDiscount > 0) return `Up to ${maxDiscount}% with ${activeCount} active promo code${activeCount === 1 ? "" : "s"}`;
    return activeCount === 1 ? "1 active promo code or deal" : `${activeCount} active promo codes & deals`;
}

// One true sentence about using the offers, in rungs of growing length; the shortest rung that reaches the window is used.
const CODE_TAILS: Array<(name: string) => string> = [
    (n) => `Copy a code and apply it at ${n} checkout.`,
    (n) => `Copy a code and paste it in the promo code field at ${n} checkout.`,
    (n) => `Copy a code from the list and paste it in the promo code field at ${n} checkout.`,
    (n) => `Read the details of an offer, then copy its code and paste it in the promo code field at ${n} checkout.`,
];
// A store whose live offers are all deals has no code to copy.
const DEAL_TAILS: Array<(name: string) => string> = [
    (n) => `Open a deal to see how to claim it at ${n}.`,
    (n) => `Open a deal on this page to see the details and how to claim it at ${n}.`,
    (n) => `Open a deal on this page to read its details and see how to claim it at ${n} before you order.`,
];
// Used only when the text is just under the window and even the first rung would pass the soft maximum.
const CODE_SHORT_TAIL = (n: string) => `Apply a code at ${n} checkout.`;
const DEAL_SHORT_TAIL = (n: string) => `Open a deal to claim it at ${n}.`;

/** The sentence that lifts `text` into the window, or null when the text is long enough (or no sentence fits under the soft maximum). */
export function pickDescriptionTail(text: string, name: string, dealsOnly: boolean): string | null {
    if (text.length >= LEGACY_META_V2_MIN) return null;
    const rungs = (dealsOnly ? DEAL_TAILS : CODE_TAILS).map((make) => make(name));
    const fits = (tail: string) => text.length + 1 + tail.length <= LEGACY_META_V2_SOFT_MAX;
    const fitting = rungs.filter(fits);
    if (fitting.length > 0) return fitting.find((tail) => text.length + 1 + tail.length >= LEGACY_META_V2_MIN) ?? fitting[fitting.length - 1];
    const short = (dealsOnly ? DEAL_SHORT_TAIL : CODE_SHORT_TAIL)(name);
    return fits(short) ? short : null;
}

// A cut title must not end on one of these ("Free month on annual VPS plans with a ...").
const DANGLING_WORDS = new Set(["a", "an", "the", "to", "at", "and", "or", "of", "for", "in", "on", "with", "your", "it", "is", "are", "that", "this", "as", "by", "from", "&"]);

/** The deal title cut at a word boundary to fit `room` characters (with "..."), or null when fewer than three words remain. */
function shortenDealTitle(title: string, room: number): string | null {
    if (title.length <= room) return title;
    const budget = room - 3;
    if (budget <= 0) return null;
    const head = title.slice(0, budget + 1);
    const words = head.slice(0, Math.max(head.lastIndexOf(" "), 0)).replace(/[\s,;:\-–—(\[]+$/, "").split(/\s+/).filter(Boolean);
    while (words.length > 0 && DANGLING_WORDS.has(words[words.length - 1].toLowerCase())) words.pop();
    return words.length >= 3 ? `${words.join(" ").replace(/[,;:\-–—(\[]+$/, "")}...` : null;
}

export interface LegacyDescriptionV2Input {
    name: string;
    /** "" or " (October 2026)". */
    period: string;
    /** "" or the check-mark prefix, decided by the caller exactly as on the old template. */
    verifiedPrefix: string;
    /** The trusted best percentage (0 = none). */
    maxDiscount: number;
    activeCount: number;
    /** The titles that may follow "Latest Deal", best first, already in their public wording (exclusive wording per rule 4). */
    dealTitles: string[];
    /** Every live offer is a deal (type DEAL): the closing sentence does not talk about codes. */
    dealsOnly: boolean;
}

/**
 * The v2 description: "{Verified }{Name} Coupon Codes{period}: {claim}.{ one helpful sentence} 🟢 Latest Deal: [{title}]".
 * The first usable deal title is quoted (generic and sub-1% titles are skipped, the segment is dropped when none is left);
 * a description under 120 characters gets one sentence about using the offers, placed before the Latest Deal segment;
 * the result never exceeds 160 characters.
 */
export function buildLegacyDescriptionV2(input: LegacyDescriptionV2Input): string {
    const lead = `${input.verifiedPrefix}${input.name} Coupon Codes${input.period}: ${legacyClaimV2(input.activeCount, input.maxDiscount)}.`;
    const marker = " \u{1F7E2} Latest Deal: [";

    let deal = "";
    const dealTitle = input.dealTitles.find(isUsableLatestDealTitle);
    if (dealTitle) {
        const room = LEGACY_META_V2_SOFT_MAX - lead.length - marker.length - 1; // the closing bracket
        const shown = shortenDealTitle(dealTitle, room);
        if (shown) deal = `${marker}${shown}]`;
    }

    const tail = pickDescriptionTail(`${lead}${deal}`, input.name, input.dealsOnly);
    let description = `${lead}${tail ? ` ${tail}` : ""}${deal}`;
    if (description.length > LEGACY_META_V2_HARD_MAX) description = description.slice(0, LEGACY_META_V2_HARD_MAX - 3) + "...";
    return description;
}
